import { ApolloClient, InMemoryCache, HttpLink, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import {
  GRAPHQL_URI,
  DEFAULT_PRICE_MODEL,
  DEFAULT_SITE_ID,
} from '@global/App/constants/constants';

import { getFormattedLocale, parseLocale } from '@global/utils/lang';
import { RootState, store } from '@global/store/store';
import { CountryService } from '@global/utils/CountryService';
import { getTokensFromCookies } from '@global/hooks/useAuth/utils';
import { SiteConfig } from '../utils/SiteConfig';
import { SiteId } from 'wly-ecommerce-common';

const errorLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors) {
    graphQLErrors.map(({ message }) => {
      console.error(`GraphQL error ${message}`);
    });
  }
});

const makeLocaleHeaders = () => {
  const { locale, siteId } = SiteConfig.getConfig();

  const { country } = store.getState() as RootState;
  const currentCountry = CountryService.getCountryByIsoCode(
    country.countries,
    parseLocale(locale)[1]
  );
  return {
    'X-Viax-User-Locale': getFormattedLocale() || '',
    'X-Viax-User-Website': siteId || DEFAULT_SITE_ID,
    'X-Viax-User-Currency': currentCountry?.currency || '',
  };
};

const headersLink = setContext((_, { headers }) => {
  const localeHeaders = makeLocaleHeaders();
  const token = getTokensFromCookies().viaxToken;
  const { siteId, pricingDeterminationModel } = SiteConfig.getConfig();

  const supportedSiteIds = Object.values(SiteId);
  const pricingModel = supportedSiteIds.includes(siteId)
    ? {
        'X-Viax-User-Pricing-Model':
          pricingDeterminationModel || DEFAULT_PRICE_MODEL,
      }
    : {};

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
      ...localeHeaders,
      ...pricingModel,
    },
  };
});

const link = from([errorLink, new HttpLink({ uri: GRAPHQL_URI })]);

const typePolicies = {
  ViaxCustomerOrder: {
    keyFields: ['uid'],
  },
  CustomerOrder: {
    keyFields: ['uid'],
  },
  CustomerOrderItem: {
    keyFields: ['biiId'],
  },
  Product: {
    keyFields: ['sku'],
  },
  WileyBookAuthor: {
    keyFields: ['uid'],
  },
};

export const client = new ApolloClient({
  link: headersLink.concat(link),
  cache: new InMemoryCache({
    typePolicies,
  }),
});
